// src/features/tag/useTagManageMutations.ts

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TagApi } from './TagApi';
import { TAG_QUERY_KEYS } from '@/lib/constants/tag';
import { MEDIA_QUERY_KEYS } from '@/lib/constants/media';
import { useAuth } from '@/features/auth/hooks/useAuth';

/**
 * Invalidation is scoped to what the mutation actually touched: tag keys
 * always (vocabulary + usage counts), but the heavyweight media lists (33k+
 * rows on large accounts) and playlists only when media/list rows changed —
 * cleaning up 15 unused tags must not refetch the video library 15 times.
 */
interface TagChangeScope {
  touchedMedia: boolean;
  touchedLists: boolean;
}

function useInvalidateAfterTagChange() {
  const queryClient = useQueryClient();
  const { selectedCId } = useAuth();
  return (scope: TagChangeScope) => {
    void queryClient.invalidateQueries({ queryKey: TAG_QUERY_KEYS.all });
    if (selectedCId == null) return;
    if (scope.touchedMedia) {
      void queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEYS.videos(selectedCId) });
      void queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEYS.streams(selectedCId) });
    }
    if (scope.touchedLists) {
      void queryClient.invalidateQueries({ queryKey: MEDIA_QUERY_KEYS.playlists(selectedCId) });
    }
  };
}

export interface RenameTagVariables {
  tagId: number;
  label: string;
  /** Whether the tag is on any media (label rides on media rows). */
  hadMediaRefs: boolean;
  /** Whether smart playlists reference the tag (rule labels re-render). */
  hadListRefs: boolean;
}

export function useRenameTag() {
  const { selectedCId } = useAuth();
  const invalidate = useInvalidateAfterTagChange();
  return useMutation({
    mutationFn: async ({ tagId, label }: RenameTagVariables) => {
      return TagApi.renameTag(selectedCId!, tagId, label);
    },
    onSettled: (_data, _error, variables) => {
      invalidate({ touchedMedia: variables.hadMediaRefs, touchedLists: variables.hadListRefs });
    },
  });
}

export function useDeleteTag() {
  const { selectedCId } = useAuth();
  const invalidate = useInvalidateAfterTagChange();
  return useMutation({
    mutationFn: async ({ tagId }: { tagId: number }) => {
      return TagApi.deleteTag(selectedCId!, tagId);
    },
    onSettled: (data) => {
      invalidate({
        // On error (data undefined) invalidate everything — state is unknown.
        touchedMedia: data ? data.removedFromMediaCount > 0 : true,
        touchedLists: data ? data.removedFromListCount > 0 : true,
      });
    },
  });
}
